import React, { RefObject, useEffect, useRef, useState } from "react";
import { GridColumn } from "./columns";
import { loadColumnWidths, saveColumnWidths } from "./state";

// Legacy flexigrid's p.minwidth (flexigrid.js:63). Must stay <= 32, the shipped
// width of the icon-only lock/key_info columns, or their first drag would jump.
const MIN_COLUMN_WIDTH = 30;
// No legacy equivalent — stops a runaway drag pushing a column's own grip
// off-screen, which would be unrecoverable without a hard refresh.
const MAX_COLUMN_WIDTH = 1200;
// Keep in sync with .monitor-col-resize-guide's width.
const GUIDE_WIDTH = 5;

interface DragState {
  columnKey: string;
  startX: number;
  baseWidth: number;
  // The dragged column's right edge, relative to the `.flexigrid` wrapper.
  guideOrigin: number;
  maxGuideLeft: number;
  nextWidth: number;
}

export interface ColumnWidths {
  widthOf: (column: GridColumn) => number;
  beginResize: (column: GridColumn, event: React.MouseEvent<HTMLElement>) => void;
  guideRef: RefObject<HTMLDivElement | null>;
  consumeSortSuppression: () => boolean;
}

function clampWidth(width: number): number {
  return Math.min(Math.max(Math.round(width), MIN_COLUMN_WIDTH), MAX_COLUMN_WIDTH);
}

// Re-clamp on load: state.ts only validates shape, so a width persisted under
// different bounds would otherwise survive out of range.
function sanitize(widths: Record<string, number> | null): Record<string, number> {
  const clamped: Record<string, number> = {};
  Object.keys(widths || {}).forEach((key) => {
    clamped[key] = clampWidth((widths as Record<string, number>)[key]);
  });
  return clamped;
}

/**
 * HHMCOVID-6343 — restores 2.10's drag-to-resize columns, which the React grid
 * never had (2.10 was legacy flexigrid, whose `.cDrag` grips are built in).
 *
 * Like 2.10, the grid does NOT resize live: during the drag only a guide line
 * follows the cursor and the width lands on release (flexigrid.js:338-355 vs
 * :398-421). That is also a performance requirement — the grid renders ~20
 * columns x up to 200 rows of non-trivial cells, so a setState per mousemove
 * would re-render thousands of elements per frame. The guide is moved through
 * `guideRef` instead, making the whole gesture cost one re-render.
 *
 * Not passed the column list on purpose: it is rebuilt on every fetch, and
 * `widthOf` reads the default off whichever column object it is handed.
 */
export function useColumnWidths(wrapperRef: RefObject<HTMLDivElement | null>, stateKey: string): ColumnWidths {
  const [widths, setWidths] = useState<Record<string, number>>(() => sanitize(loadColumnWidths(stateKey)));
  // Mirrors `widths` so the permanently-bound listeners below don't close over
  // a stale value.
  const widthsRef = useRef(widths);
  const dragRef = useRef<DragState | null>(null);
  const suppressSortClickRef = useRef(false);
  const guideRef = useRef<HTMLDivElement | null>(null);

  const widthOf = (column: GridColumn) => {
    const override = widths[column.key];
    return override != null ? override : column.width;
  };

  const consumeSortSuppression = () => {
    const suppressed = suppressSortClickRef.current;
    suppressSortClickRef.current = false;
    return suppressed;
  };

  const endDragChrome = () => {
    const guide = guideRef.current;
    if (guide) {
      // Cleared, not set to "none", so the stylesheet's own `display: none` hides
      // it — the inline style is only ever this hook's live override.
      guide.style.display = "";
    }
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };

  // Bound once and left bound, guarded on `dragRef.current`, as legacy did
  // (flexigrid.js:1722-1724): no per-gesture listener churn, no stale closures.
  useEffect(() => {
    const onMouseMove = (event: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) {
        return;
      }
      const width = clampWidth(drag.baseWidth + (event.clientX - drag.startX));
      drag.nextWidth = width;
      const guide = guideRef.current;
      if (!guide) {
        return;
      }
      // Tracks the clamped width so the line stops at the floor/ceiling instead of
      // sliding on (legacy only moved its grip while nw > p.minwidth). The outer
      // clamp keeps it inside `.flexigrid`, which is `overflow: hidden`.
      const left = drag.guideOrigin + (width - drag.baseWidth);
      guide.style.left = `${Math.max(0, Math.min(left, drag.maxGuideLeft))}px`;
    };

    const onMouseUp = () => {
      const drag = dragRef.current;
      if (!drag) {
        return;
      }
      dragRef.current = null;
      endDragChrome();

      if (drag.nextWidth !== drag.baseWidth) {
        const next = { ...widthsRef.current, [drag.columnKey]: drag.nextWidth };
        widthsRef.current = next;
        setWidths(next); // the gesture's only re-render
        saveColumnWidths(stateKey, next);
      }

      // A drag released outside any header produces no click to consume the flag,
      // so drop it on the next task — timers run after click dispatch — or it
      // would swallow the user's next genuine header click.
      window.setTimeout(() => {
        suppressSortClickRef.current = false;
      }, 0);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    // A mouseup delivered outside this document never reaches us, which would
    // leave the guide pinned and the body cursor stuck. Legacy ended its drag on
    // document hover-out for the same reason.
    document.addEventListener("mouseleave", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("mouseleave", onMouseUp);
      // Unmounting mid-drag must not leave `col-resize` and an unselectable
      // <body> behind, nor a set flag that would eat the next header click.
      dragRef.current = null;
      suppressSortClickRef.current = false;
      endDragChrome();
    };
  }, [stateKey]);

  const beginResize = (column: GridColumn, event: React.MouseEvent<HTMLElement>) => {
    if (event.button !== 0) {
      return;
    }
    // Kills the text selection / native drag this gesture would otherwise start
    // (legacy used $('body').noSelect()).
    event.preventDefault();
    event.stopPropagation();

    const th = event.currentTarget.parentElement;
    const wrapper = wrapperRef.current;
    const guide = guideRef.current;
    if (!th || !wrapper || !guide) {
      return;
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    const guideOrigin = th.getBoundingClientRect().right - wrapperRect.left;
    const maxGuideLeft = Math.max(0, wrapperRect.width - GUIDE_WIDTH);
    // The model width, never a measured rect. Legacy persisted the inner div's
    // offsetWidth and restored it as a content width (flexigrid.js:465-470), so
    // its columns grew slightly on every session round-trip.
    const baseWidth = widthOf(column);

    dragRef.current = { columnKey: column.key, startX: event.clientX, baseWidth, guideOrigin, maxGuideLeft, nextWidth: baseWidth };

    // Consumed by the <th>'s onClick, and that is the real guarantee a resize
    // never sorts: `click` fires on the nearest common ancestor of mousedown and
    // mouseup, so a short drag released over the header's label resolves to the
    // <th> and the grip's own stopPropagation never runs.
    suppressSortClickRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    guide.style.display = "block";
    guide.style.left = `${Math.max(0, Math.min(guideOrigin, maxGuideLeft))}px`;
  };

  return { widthOf, beginResize, guideRef, consumeSortSuppression };
}
